import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Phone, Mail, MapPin, ArrowRight, Check, X, Clock, FolderPlus, CheckCircle2, Users, Flag, CalendarDays, CalendarPlus, Pencil, Ban } from "lucide-react";
import { toast } from "sonner";
import { PROSPECT_STATUSES } from "@/data/partnerConfig";
import { usePartner, PIPELINE_LABELS, APPOINTMENT_TYPE_LABELS, APPOINTMENT_LOCATION_LABELS } from "../PartnerContext";
import { Card, PageHeader, Btn, Segmented, TypeBadge, StatusBadge, Pill, SlideOver, Field, inputCls, InfoRow } from "../ui";
import { AppointmentModal } from "../components/AppointmentModal";

const EMPTY = { name: "", type: "moe", source: "Partenaire", status: "Nouveau", city: "", project: "", budget: "", phone: "", email: "" };
const EMPTY_OWN_PROSPECT = {
  customer_type: "INDIVIDUAL", firstname: "", lastname: "", add_co_owner: false, co_owner_first_name: "", co_owner_last_name: "",
  company_name: "", company_siret: "", contact_first_name: "", contact_last_name: "",
  phone: "", email: "", address: "", postal_code: "", city: "", project_type: "", message: "", budget: "", comment: "",
};

const ROUTING_LABELS = {
  WAITING_PARTNER: "Nouveau projet", PROPOSED_TO_NEIGHBOR: "Projet proposé (secteur voisin)",
  ACCEPTED: "Accepté", NEIGHBOR_ACCEPTED: "Accepté", PARTNER_REFUSED: "Refusé", CENTRAL_REVIEW: "Transféré à la Centrale",
};
const PIPELINE_EDITABLE = ["NOUVEAU", "ATTRIBUE", "CONTACTE", "RDV", "PROPOSITION_ENVOYEE", "PERDU"];

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null;

const SlaCountdown = ({ deadline }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  if (!deadline) return null;
  const diff = new Date(deadline).getTime() - now;
  if (diff <= 0) return <span data-testid="sla-expired">Délai expiré — repasse à la Centrale</span>;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return <span data-testid="sla-countdown">Répondre avant {h}h {m}min ({fmtDateTime(deadline)})</span>;
};

const CollaborationRequestForm = ({ prospectId, onDone }) => {
  const { lookupTerritoryPartner, requestCollaboration } = usePartner();
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [found, setFound] = useState(null);
  const [checking, setChecking] = useState(false);

  const check = async () => {
    setChecking(true);
    const res = await lookupTerritoryPartner(postalCode, city);
    setFound(res.found ? res : { found: false });
    setChecking(false);
  };
  const send = async () => {
    if (!found?.found) return;
    const ok = await requestCollaboration(prospectId, found.partner_id);
    if (ok) onDone();
  };
  return (
    <div className="space-y-3 rounded-xl border border-[#E3E8E9] bg-petrol-bg/40 p-4">
      <p className="text-xs font-bold uppercase text-petrol">Demander la collaboration d'un partenaire</p>
      <div className="grid gap-2 sm:grid-cols-2">
        <input data-testid="collab-postal-code" className={inputCls} placeholder="Code postal du chantier" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
        <input data-testid="collab-city" className={inputCls} placeholder="Commune du chantier" value={city} onChange={(e) => setCity(e.target.value)} />
      </div>
      <Btn variant="ghost" testid="collab-check" onClick={check} disabled={checking}>Rechercher le partenaire du secteur</Btn>
      {found && (found.found ? (
        <div className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm">
          <span>{found.partner_name} — {found.territory_name}</span>
          <Btn testid="collab-send" onClick={send}>Envoyer la demande</Btn>
        </div>
      ) : (
        <p data-testid="collab-not-found" className="text-xs text-stone-500">Aucun partenaire actif trouvé pour cette commune.</p>
      ))}
    </div>
  );
};

const STATUS_PILL_TONE = { PLANIFIE: "petrol", REALISE: "sage", ANNULE: "stone" };
const STATUS_LABEL = { PLANIFIE: "Prévu", REALISE: "Réalisé", ANNULE: "Annulé" };

export const AppointmentsPanel = ({ prospectId }) => {
  const { appointments, setAppointmentStatus, deleteAppointment } = usePartner();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const list = appointments.filter((a) => a.prospect_id === prospectId);
  const upcoming = list.filter((a) => a.status === "PLANIFIE").sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  const history = list.filter((a) => a.status !== "PLANIFIE").sort((a, b) => new Date(b.start_at) - new Date(a.start_at));
  const next = upcoming[0];

  return (
    <div data-testid={`appointments-panel-${prospectId}`} className="mt-3 rounded-xl border border-[#E3E8E9] bg-petrol-bg/30 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase text-petrol">Rendez-vous</p>
        <Btn variant="ghost" testid={`appointment-create-open-${prospectId}`} onClick={() => { setEditing(null); setModalOpen(true); }}>
          <CalendarPlus className="h-3.5 w-3.5" /> Prendre rendez-vous
        </Btn>
      </div>
      {next ? (
        <div data-testid={`appointment-next-${prospectId}`} className="mt-3 rounded-lg bg-white p-3">
          <p className="text-sm font-bold text-navy">{fmtDateTime(next.start_at)} — {APPOINTMENT_TYPE_LABELS[next.appointment_type]}</p>
          <p className="mt-0.5 text-xs text-stone-500">{APPOINTMENT_LOCATION_LABELS[next.location_type]}{next.location_address ? ` · ${next.location_address}` : ""}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Btn variant="ghost" testid={`appointment-edit-${next.id}`} onClick={() => { setEditing(next); setModalOpen(true); }}><Pencil className="h-3 w-3" /> Modifier</Btn>
            <Btn variant="ghost" testid={`appointment-done-${next.id}`} onClick={() => setAppointmentStatus(next.id, "REALISE")}><CheckCircle2 className="h-3 w-3" /> Marquer réalisé</Btn>
            <Btn variant="ghost" testid={`appointment-cancel-${next.id}`} onClick={() => setAppointmentStatus(next.id, "ANNULE")}><Ban className="h-3 w-3" /> Annuler</Btn>
            <Btn variant="ghost" testid={`appointment-delete-${next.id}`} onClick={() => deleteAppointment(next.id)}><X className="h-3 w-3" /> Supprimer</Btn>
          </div>
        </div>
      ) : (
        <p className="mt-3 text-xs text-stone-500" data-testid={`appointment-none-${prospectId}`}>Aucun rendez-vous planifié.</p>
      )}
      {upcoming.length > 1 && (
        <ul className="mt-2 space-y-1 text-xs text-stone-500">
          {upcoming.slice(1).map((a) => (
            <li key={a.id} className="flex items-center justify-between">
              <span>{fmtDateTime(a.start_at)} — {APPOINTMENT_TYPE_LABELS[a.appointment_type]}</span>
              <Pill tone="white">{STATUS_LABEL[a.status]}</Pill>
            </li>
          ))}
        </ul>
      )}
      {history.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-bold uppercase text-stone-400">Historique</p>
          <ul className="mt-2 space-y-1.5" data-testid={`appointment-history-${prospectId}`}>
            {history.map((a) => (
              <li key={a.id} className="flex items-center justify-between text-xs text-stone-500">
                <span>{fmtDateTime(a.start_at)} — {APPOINTMENT_TYPE_LABELS[a.appointment_type]}</span>
                <Pill tone={STATUS_PILL_TONE[a.status]}>{STATUS_LABEL[a.status]}</Pill>
              </li>
            ))}
          </ul>
        </div>
      )}
      <AppointmentModal open={modalOpen} onClose={() => setModalOpen(false)} prospectId={prospectId} editing={editing} />
    </div>
  );
};

const TerritorialProspectRow = ({ p, onAccept, onRefuse, onValidateClient, onKeepProspect, busy, validating }) => {
  const { updatePipelineStage, respondCollaboration, appointments } = usePartner();
  const navigate = useNavigate();
  const [collabOpen, setCollabOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const actionable = ["WAITING_PARTNER", "PROPOSED_TO_NEIGHBOR"].includes(p.routingStatus);
  const accepted = ["ACCEPTED", "NEIGHBOR_ACCEPTED"].includes(p.routingStatus);
  const isClient = !!p.clientProfileId;
  const canBookAppointment = p.routingStatus !== "PARTNER_REFUSED";
  const appointmentCount = appointments.filter((a) => a.prospect_id === p.id && a.status === "PLANIFIE").length;
  return (
    <div data-testid={`territorial-prospect-${p.id}`} className={`rounded-2xl border-2 p-5 ${actionable ? "border-[#E3E8E9] bg-white" : "border-[#EDF0F1] bg-white/60"}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-xs font-bold uppercase text-petrol">{ROUTING_LABELS[p.routingStatus] || p.routingStatus}</p>
            <Pill tone="white">{p.source}</Pill>
            {p.customerType === "COMPANY" && <Pill tone="copper">Entreprise</Pill>}
            {accepted && !isClient && <Pill tone="sage">{PIPELINE_LABELS[p.pipelineStage] || p.pipelineStage}</Pill>}
          </div>
          <p className="mt-1 text-sm font-bold text-navy">{p.city} — {p.project}</p>
          <p className="mt-1 text-xs text-stone-500">
            {p.name}{p.coOwnerFirstName || p.coOwnerLastName ? ` & ${p.coOwnerFirstName} ${p.coOwnerLastName}`.trim() : ""} · {p.budget || "Budget non précisé"}
          </p>
        </div>
        {actionable && p.slaDeadline && (
          <span data-testid={`sla-badge-${p.id}`} className="flex shrink-0 items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-[11px] font-bold text-amber-700">
            <Clock className="h-3 w-3" /> <SlaCountdown deadline={p.slaDeadline} />
          </span>
        )}
      </div>
      {actionable && (
        <div className="mt-3 flex gap-2">
          <Btn testid={`territorial-accept-${p.id}`} disabled={busy === p.id} onClick={() => onAccept(p.id)}><Check className="h-3.5 w-3.5" /> Accepter</Btn>
          <Btn variant="ghost" testid={`territorial-refuse-${p.id}`} disabled={busy === p.id} onClick={() => onRefuse(p.id)}><X className="h-3.5 w-3.5" /> Refuser</Btn>
        </div>
      )}
      {accepted && !isClient && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select data-testid={`pipeline-select-${p.id}`} className={`${inputCls} w-auto`} value={p.pipelineStage} onChange={(e) => updatePipelineStage(p.id, e.target.value)}>
            {PIPELINE_EDITABLE.map((s) => <option key={s} value={s}>{PIPELINE_LABELS[s]}</option>)}
          </select>
          <Btn testid={`territorial-validate-client-${p.id}`} disabled={validating === p.id} onClick={() => onValidateClient(p.id)}><FolderPlus className="h-3.5 w-3.5" /> Valider comme client</Btn>
          <Btn variant="ghost" testid={`territorial-keep-prospect-${p.id}`} disabled={validating === p.id} onClick={() => onKeepProspect(p.id)}>Conserver comme prospect</Btn>
          {p.followupStatus === "NONE" || p.followupStatus === "REFUSED" ? (
            <Btn variant="ghost" testid={`collab-open-${p.id}`} onClick={() => setCollabOpen((v) => !v)}><Users className="h-3.5 w-3.5" /> Collaboration</Btn>
          ) : (
            <Pill tone={p.followupStatus === "ACCEPTED" ? "sage" : "white"}>Collaboration {p.followupStatus === "ACCEPTED" ? "acceptée" : "en attente"}</Pill>
          )}
        </div>
      )}
      {isClient && (
        <div className="mt-3 flex items-center gap-3">
          <span data-testid={`territorial-client-validated-${p.id}`} className="flex items-center gap-1.5 text-xs font-bold text-petrol"><CheckCircle2 className="h-3.5 w-3.5" /> Validé comme client</span>
          <Btn variant="ghost" testid={`territorial-open-client-${p.id}`} onClick={() => navigate(`/partenaire/clients?open=${p.clientProfileId}`)}>
            <ArrowRight className="h-3.5 w-3.5" /> Voir la fiche client
          </Btn>
        </div>
      )}
      {canBookAppointment && (
        <div className="mt-3">
          <Btn variant="ghost" testid={`appointment-toggle-${p.id}`} onClick={() => setApptOpen((v) => !v)}>
            <CalendarDays className="h-3.5 w-3.5" /> Rendez-vous{appointmentCount > 0 ? ` (${appointmentCount})` : ""}
          </Btn>
        </div>
      )}
      {apptOpen && <AppointmentsPanel prospectId={p.id} />}
      {collabOpen && <div className="mt-3"><CollaborationRequestForm prospectId={p.id} onDone={() => setCollabOpen(false)} /></div>}
      {p.followupStatus === "REQUESTED" && (
        <div className="mt-3 flex items-center justify-between rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <span>Demande de collaboration envoyée, en attente de réponse</span>
        </div>
      )}
    </div>
  );
};

const IncomingCollaborationCard = ({ p, onRespond }) => (
  <div data-testid={`collab-incoming-${p.id}`} className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-5">
    <p className="text-xs font-bold uppercase text-amber-700">Demande de collaboration inter-territoire</p>
    <p className="mt-1 text-sm font-bold text-navy">{p.city} — {p.project}</p>
    <p className="mt-1 text-xs text-stone-500">Un partenaire souhaite que vous participiez au suivi de ce chantier. Vous ne deviendrez pas propriétaire commercial du client.</p>
    <div className="mt-3 flex gap-2">
      <Btn testid={`collab-accept-${p.id}`} onClick={() => onRespond(p.id, true)}><Check className="h-3.5 w-3.5" /> Accepter</Btn>
      <Btn variant="ghost" testid={`collab-refuse-${p.id}`} onClick={() => onRespond(p.id, false)}><X className="h-3.5 w-3.5" /> Refuser</Btn>
    </div>
  </div>
);

const CreateOwnProspectForm = ({ open, onClose }) => {
  const { createOwnProspect, signalDuplicate } = usePartner();
  const [form, setForm] = useState(EMPTY_OWN_PROSPECT);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setDuplicateInfo(null);
    if (form.customer_type === "COMPANY") {
      if (!form.company_name.trim() || !form.phone.trim()) {
        toast.error("La raison sociale et le téléphone sont nécessaires.");
        return;
      }
    } else if (!form.firstname.trim() || !form.lastname.trim() || !form.phone.trim()) {
      toast.error("Prénom, nom et téléphone sont nécessaires.");
      return;
    }
    const { add_co_owner, ...payload } = form;
    if (!add_co_owner) {
      payload.co_owner_first_name = "";
      payload.co_owner_last_name = "";
    }
    const res = await createOwnProspect(payload);
    if (res.ok) {
      setForm(EMPTY_OWN_PROSPECT);
      onClose();
    } else if (res.duplicate) {
      setDuplicateInfo(res.message);
    }
  };

  return (
    <SlideOver open={open} onClose={onClose} testid="own-prospect-create" eyebrow="Apport commercial" title="Nouveau prospect">
      <form onSubmit={submit} className="space-y-4" data-testid="own-prospect-form">
        <Field label="Type de client">
          <Segmented
            testid="own-prospect-customer-type"
            size="sm"
            value={form.customer_type}
            onChange={(v) => setForm((f) => ({ ...f, customer_type: v }))}
            options={[{ value: "INDIVIDUAL", label: "Particulier" }, { value: "COMPANY", label: "Entreprise" }]}
          />
        </Field>

        {form.customer_type === "INDIVIDUAL" ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Prénom *"><input data-testid="own-prospect-firstname" className={inputCls} value={form.firstname} onChange={set("firstname")} /></Field>
              <Field label="Nom *"><input data-testid="own-prospect-lastname" className={inputCls} value={form.lastname} onChange={set("lastname")} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-600" data-testid="own-prospect-co-owner-toggle">
              <input
                type="checkbox"
                checked={form.add_co_owner}
                onChange={(e) => setForm((f) => ({ ...f, add_co_owner: e.target.checked }))}
                className="h-4 w-4 rounded border-[#D9E0E2] text-petrol focus:ring-petrol"
              />
              Ajouter un deuxième propriétaire / co-acquéreur
            </label>
            {form.add_co_owner && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Prénom du deuxième propriétaire"><input data-testid="own-prospect-co-owner-firstname" className={inputCls} value={form.co_owner_first_name} onChange={set("co_owner_first_name")} /></Field>
                <Field label="Nom du deuxième propriétaire"><input data-testid="own-prospect-co-owner-lastname" className={inputCls} value={form.co_owner_last_name} onChange={set("co_owner_last_name")} /></Field>
              </div>
            )}
          </>
        ) : (
          <>
            <Field label="Raison sociale *"><input data-testid="own-prospect-company-name" className={inputCls} value={form.company_name} onChange={set("company_name")} /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Prénom du contact"><input data-testid="own-prospect-contact-firstname" className={inputCls} value={form.contact_first_name} onChange={set("contact_first_name")} /></Field>
              <Field label="Nom du contact"><input data-testid="own-prospect-contact-lastname" className={inputCls} value={form.contact_last_name} onChange={set("contact_last_name")} /></Field>
            </div>
            <Field label="SIRET"><input data-testid="own-prospect-siret" className={inputCls} value={form.company_siret} onChange={set("company_siret")} /></Field>
          </>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Téléphone *"><input data-testid="own-prospect-phone" className={inputCls} value={form.phone} onChange={set("phone")} /></Field>
          <Field label="Email"><input data-testid="own-prospect-email" className={inputCls} value={form.email} onChange={set("email")} /></Field>
        </div>
        <Field label="Adresse actuelle"><input data-testid="own-prospect-address" className={inputCls} value={form.address} onChange={set("address")} /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Code postal"><input data-testid="own-prospect-postal" className={inputCls} value={form.postal_code} onChange={set("postal_code")} /></Field>
          <Field label="Commune"><input data-testid="own-prospect-city" className={inputCls} value={form.city} onChange={set("city")} /></Field>
        </div>
        <Field label="Type de projet"><input data-testid="own-prospect-project-type" className={inputCls} value={form.project_type} onChange={set("project_type")} placeholder="Construction, extension…" /></Field>
        <Field label="Budget estimatif"><input data-testid="own-prospect-budget" className={inputCls} value={form.budget} onChange={set("budget")} /></Field>
        <Field label="Besoin / description"><textarea data-testid="own-prospect-message" rows={3} className={inputCls} value={form.message} onChange={set("message")} /></Field>
        <Field label="Commentaire"><textarea data-testid="own-prospect-comment" rows={2} className={inputCls} value={form.comment} onChange={set("comment")} /></Field>
        {duplicateInfo && (
          <div data-testid="own-prospect-duplicate-alert" className="space-y-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-700">
            <p>{duplicateInfo}</p>
            <Btn variant="ghost" testid="own-prospect-signal-duplicate" onClick={async () => {
              const ok = await signalDuplicate(form.email, form.phone, "Tentative de création par un autre partenaire");
              if (ok) setDuplicateInfo(null);
            }}>
              <Flag className="h-3.5 w-3.5" /> Signaler / demander une mise en relation
            </Btn>
          </div>
        )}
        <Btn type="submit" testid="own-prospect-submit" className="w-full">Créer le prospect</Btn>
      </form>
    </SlideOver>
  );
};

const TerritorialProspectsView = () => {
  const { territorialProspects, collaborationsIncoming, acceptTerritorialProspect, refuseTerritorialProspect, validateProspectAsClient, respondCollaboration } = usePartner();
  const [busy, setBusy] = useState(null);
  const [validatingId, setValidatingId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const act = async (id, action) => {
    setBusy(id);
    await (action === "accept" ? acceptTerritorialProspect(id) : refuseTerritorialProspect(id));
    setBusy(null);
  };
  const validateClient = async (id) => {
    setValidatingId(id);
    await validateProspectAsClient(id);
    setValidatingId(null);
  };
  const keepProspect = () => {
    toast.info("Prospect conservé — vous pourrez le valider comme client plus tard.");
  };

  const actionable = territorialProspects.filter((p) => ["WAITING_PARTNER", "PROPOSED_TO_NEIGHBOR"].includes(p.routingStatus));
  const others = territorialProspects.filter((p) => !["WAITING_PARTNER", "PROPOSED_TO_NEIGHBOR"].includes(p.routingStatus));

  return (
    <div data-testid="partner-territorial-prospects">
      <PageHeader
        eyebrow="Développement commercial" title="Prospects"
        sub="Projets réellement routés par CozyHome sur votre secteur, et prospects que vous apportez vous-même."
        actions={
          <Btn testid="own-prospect-create-open" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Nouveau prospect
          </Btn>
        }
      />
      {collaborationsIncoming.length > 0 && (
        <div data-testid="collab-incoming-list" className="mb-4 space-y-3">
          {collaborationsIncoming.map((p) => <IncomingCollaborationCard key={p.id} p={p} onRespond={respondCollaboration} />)}
        </div>
      )}
      {territorialProspects.length === 0 ? (
        <Card testid="territorial-prospect-empty" className="p-10 text-center text-sm text-stone-500">Aucun projet transmis pour le moment sur votre secteur.</Card>
      ) : (
        <div data-testid="territorial-prospect-list" className="space-y-3">
          {actionable.map((p) => <TerritorialProspectRow key={p.id} p={p} onAccept={(id) => act(id, "accept")} onRefuse={(id) => act(id, "refuse")} onValidateClient={validateClient} onKeepProspect={keepProspect} busy={busy} validating={validatingId} />)}
          {others.map((p) => <TerritorialProspectRow key={p.id} p={p} onAccept={(id) => act(id, "accept")} onRefuse={(id) => act(id, "refuse")} onValidateClient={validateClient} onKeepProspect={keepProspect} busy={busy} validating={validatingId} />)}
        </div>
      )}
      <CreateOwnProspectForm open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
};

const ProspectCard = ({ p, onOpen }) => (
  <button
    data-testid={`prospect-card-${p.id}`}
    onClick={() => onOpen(p)}
    className="group w-full rounded-2xl border border-[#E3E8E9] bg-white p-4 text-left transition-colors hover:border-petrol/40"
  >
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="truncate font-semibold text-navy">{p.name}</p>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-stone-500"><MapPin className="h-3 w-3" /> {p.city}</p>
      </div>
      <StatusBadge status={p.status} />
    </div>
    <p className="mt-3 line-clamp-1 text-sm text-stone-600">{p.project}</p>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <TypeBadge type={p.type} />
      <Pill tone={p.source === "CozyHome" ? "sage" : "white"}>{p.source === "CozyHome" ? "Source CozyHome" : "Créé par moi"}</Pill>
      <span className="ml-auto text-[11px] text-stone-400">{p.createdAt}</span>
    </div>
    <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-petrol">
      <ArrowRight className="h-3.5 w-3.5" /> {p.nextAction}
    </p>
  </button>
);

export default function PartnerProspects() {
  const { backend, territorial } = usePartner();
  if (backend && territorial) return <TerritorialProspectsView />;
  return <DemoProspectsView />;
}

const DemoProspectsView = () => {
  const { prospects, addProspect, can, backend } = usePartner();
  const [type, setType] = useState("moe");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("Tous");
  const [source, setSource] = useState("Toutes");
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const list = useMemo(
    () =>
      prospects.filter(
        (p) =>
          p.type === type &&
          (status === "Tous" || p.status === status) &&
          (source === "Toutes" || p.source === source) &&
          (!q || `${p.name} ${p.city} ${p.project}`.toLowerCase().includes(q.toLowerCase()))
      ),
    [prospects, type, q, status, source]
  );

  const counts = useMemo(
    () => ({ moe: prospects.filter((p) => p.type === "moe").length, small: prospects.filter((p) => p.type === "small").length }),
    [prospects]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.city.trim()) {
      toast.error("Le nom et la commune sont nécessaires.");
      return;
    }
    const created = await addProspect({ ...form, createdAt: new Date().toLocaleDateString("fr-FR"), nextAction: "Premier appel à planifier" });
    if (!created) return;
    setType(form.type);
    setCreating(false);
    setForm(EMPTY);
    toast.success(backend ? "Prospect créé et attribué à votre compte." : "Prospect créé (données de démonstration).");
  };

  return (
    <div data-testid="partner-prospects">
      <PageHeader
        eyebrow="Développement commercial"
        title="Prospects"
        sub="Retrouvez les demandes transmises par CozyHome et celles que vous créez vous-même. Chaque prospect pourra ensuite devenir un client et un projet."
        actions={can("prospects.create") &&
          <Btn testid="prospect-create-open" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Nouveau prospect
          </Btn>
        }
      />

      <Segmented
        testid="prospect-type"
        value={type}
        onChange={setType}
        options={[
          { value: "moe", label: `Projet avec maîtrise d'œuvre (${counts.moe})` },
          { value: "small", label: `Petits travaux / sans maîtrise d'œuvre (${counts.small})` },
        ]}
      />

      <Card className="mt-4 flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <input
            data-testid="prospect-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un nom, une commune, un projet…"
            className={`${inputCls} pl-9`}
          />
        </div>
        <select data-testid="prospect-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} sm:w-48`}>
          <option>Tous</option>
          {PROSPECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="prospect-filter-source" value={source} onChange={(e) => setSource(e.target.value)} className={`${inputCls} sm:w-44`}>
          <option>Toutes</option>
          <option>CozyHome</option>
          <option>Partenaire</option>
        </select>
      </Card>

      {list.length === 0 ? (
        <Card testid="prospect-empty" className="mt-4 p-10 text-center text-sm text-stone-500">Aucun prospect ne correspond à ces critères.</Card>
      ) : (
        <div data-testid="prospect-list" className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {list.map((p) => <ProspectCard key={p.id} p={p} onOpen={setSelected} />)}
        </div>
      )}

      {/* Fiche prospect */}
      <SlideOver
        open={!!selected}
        onClose={() => setSelected(null)}
        testid="prospect-detail"
        eyebrow="Fiche prospect"
        title={selected?.name}
        footer={
          <div className="flex flex-wrap gap-2">
            <Btn testid="prospect-convert" onClick={() => toast.info("La conversion en client / projet sera connectée dans la phase suivante.")}>
              Convertir en client / projet
            </Btn>
            <Btn variant="ghost" testid="prospect-mark-lost" onClick={() => toast.info("Changement de statut simulé pour cette V1.")}>
              Changer le statut
            </Btn>
          </div>
        }
      >
        {selected && (
          <>
            <div className="flex flex-wrap gap-2">
              <TypeBadge type={selected.type} />
              <StatusBadge status={selected.status} />
              <Pill tone={selected.source === "CozyHome" ? "sage" : "white"}>Source : {selected.source}</Pill>
            </div>
            <div className="mt-5">
              <InfoRow label="Projet" value={selected.project} />
              <InfoRow label="Budget indicatif" value={selected.budget || "—"} />
              <InfoRow label="Commune" value={selected.city} />
              <InfoRow label="Créé le" value={selected.createdAt} />
              <InfoRow label="Prochaine action" value={selected.nextAction} />
            </div>
            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              <a href={`tel:${selected.phone}`} className="flex items-center gap-2 rounded-xl bg-petrol-bg px-3.5 py-3 text-sm font-semibold text-navy hover:bg-petrol-tint" data-testid="prospect-call">
                <Phone className="h-4 w-4 text-petrol" /> {selected.phone}
              </a>
              <a href={`mailto:${selected.email}`} className="flex items-center gap-2 rounded-xl bg-petrol-bg px-3.5 py-3 text-sm font-semibold text-navy hover:bg-petrol-tint" data-testid="prospect-mail">
                <Mail className="h-4 w-4 text-petrol" /> <span className="truncate">{selected.email}</span>
              </a>
            </div>
            <div className="mt-6">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">Notes</p>
              <textarea rows={4} placeholder="Ajouter une note (non enregistrée dans cette V1)…" className={`${inputCls} mt-2`} data-testid="prospect-notes" />
            </div>
          </>
        )}
      </SlideOver>

      {/* Création */}
      <SlideOver open={creating} onClose={() => setCreating(false)} testid="prospect-create" eyebrow="Nouveau prospect" title="Créer un prospect">
        <form onSubmit={submit} className="space-y-4" data-testid="prospect-create-form">
          <Field label="Type de demande">
            <Segmented
              testid="prospect-create-type"
              size="sm"
              value={form.type}
              onChange={(v) => setForm({ ...form, type: v })}
              options={[{ value: "moe", label: "Avec maîtrise d'œuvre" }, { value: "small", label: "Petits travaux" }]}
            />
          </Field>
          <Field label="Nom du prospect *"><input data-testid="prospect-create-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Famille Dupont" /></Field>
          <Field label="Commune *"><input data-testid="prospect-create-city" className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Vesoul" /></Field>
          <Field label="Projet"><input data-testid="prospect-create-project" className={inputCls} value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} placeholder="Rénovation d'une salle de bains" /></Field>
          <Field label="Budget indicatif"><input data-testid="prospect-create-budget" className={inputCls} value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} placeholder="10 000 – 15 000 €" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Téléphone"><input data-testid="prospect-create-phone" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Email"><input data-testid="prospect-create-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          </div>
          <Field label="Statut">
            <select data-testid="prospect-create-status" className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {PROSPECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Btn type="submit" testid="prospect-create-submit" className="w-full">Créer le prospect</Btn>
        </form>
      </SlideOver>
    </div>
  );
};
